/**
 * IEPGoalsSummary Component
 * Summary view of IEP-specific goals
 * BP-06-03: Care Team & Goals Frontend
 */

import React from 'react';
import { FileText, Target, TrendingUp, AlertCircle, CheckCircle } from 'lucide-react';
import type { Goal, IEP_AREAS, GOAL_STATUS_LABELS } from '@/features/goals/types/goalsTypes';

interface IEPGoalsSummaryProps {
  goals: Goal[];
  onGoalClick?: (goal: Goal) => void;
  isLoading?: boolean;
}

function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    not_started: 'bg-gray-100 text-gray-600',
    in_progress: 'bg-sage/10 text-sage-dark',
    achieved: 'bg-green-100 text-green-700',
    discontinued: 'bg-terracotta/10 text-terracotta',
  };
  return colors[status] || colors.not_started;
}

function ProgressBar({ percentage }: { percentage: number }) {
  const getColor = () => {
    if (percentage >= 80) return 'bg-green-500';
    if (percentage >= 50) return 'bg-sage';
    if (percentage >= 25) return 'bg-yellow-500';
    return 'bg-gray-300';
  };

  return (
    <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
      <div
        className={`h-full rounded-full transition-all ${getColor()}`}
        style={{ width: `${percentage}%` }}
      />
    </div>
  );
}

export function IEPGoalsSummary({ goals, onGoalClick, isLoading }: IEPGoalsSummaryProps) {
  const iepGoals = goals.filter((g) => g.isIepGoal);

  // Group by IEP area
  const goalsByArea = iepGoals.reduce((acc, goal) => {
    const area = goal.iepArea || 'Other';
    if (!acc[area]) acc[area] = [];
    acc[area].push(goal);
    return acc;
  }, {} as Record<string, Goal[]>);

  // Calculate summary stats
  const stats = {
    total: iepGoals.length,
    achieved: iepGoals.filter((g) => g.status === 'achieved').length,
    inProgress: iepGoals.filter((g) => g.status === 'in_progress').length,
    avgProgress: iepGoals.length > 0
      ? Math.round(iepGoals.reduce((sum, g) => sum + g.progressPercentage, 0) / iepGoals.length)
      : 0,
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-6 bg-cream-dark rounded w-1/3" />
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-20 bg-cream-dark rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (iepGoals.length === 0) {
    return (
      <div className="text-center py-12 bg-cream/50 rounded-lg">
        <FileText className="w-12 h-12 mx-auto mb-3 text-warm-gray opacity-50" />
        <h3 className="font-medium text-navy mb-1">No IEP Goals</h3>
        <p className="text-sm text-warm-gray">Create goals and mark them as IEP goals to see them here</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-warm-gray/10 p-4">
          <div className="flex items-center gap-2 text-warm-gray text-sm mb-1">
            <Target className="w-4 h-4" />
            Total Goals
          </div>
          <div className="text-2xl font-display font-semibold text-navy">{stats.total}</div>
        </div>
        <div className="bg-white rounded-lg border border-warm-gray/10 p-4">
          <div className="flex items-center gap-2 text-warm-gray text-sm mb-1">
            <CheckCircle className="w-4 h-4" />
            Achieved
          </div>
          <div className="text-2xl font-display font-semibold text-green-600">{stats.achieved}</div>
        </div>
        <div className="bg-white rounded-lg border border-warm-gray/10 p-4">
          <div className="flex items-center gap-2 text-warm-gray text-sm mb-1">
            <TrendingUp className="w-4 h-4" />
            In Progress
          </div>
          <div className="text-2xl font-display font-semibold text-sage">{stats.inProgress}</div>
        </div>
        <div className="bg-white rounded-lg border border-warm-gray/10 p-4">
          <div className="flex items-center gap-2 text-warm-gray text-sm mb-1">
            <AlertCircle className="w-4 h-4" />
            Avg Progress
          </div>
          <div className="text-2xl font-display font-semibold text-navy">{stats.avgProgress}%</div>
        </div>
      </div>

      {/* Goals by Area */}
      <div className="space-y-4">
        {Object.entries(goalsByArea).map(([area, areaGoals]) => (
          <div key={area} className="bg-white rounded-lg border border-warm-gray/10 overflow-hidden">
            <div className="px-4 py-3 bg-cream border-b border-warm-gray/10">
              <h3 className="font-semibold text-navy">{area}</h3>
              <p className="text-sm text-warm-gray">{areaGoals.length} goal{areaGoals.length !== 1 ? 's' : ''}</p>
            </div>
            <div className="divide-y divide-warm-gray/10">
              {areaGoals.map((goal) => (
                <div
                  key={goal.id}
                  onClick={() => onGoalClick?.(goal)}
                  className={`p-4 ${onGoalClick ? 'cursor-pointer hover:bg-cream/50' : ''} transition-colors`}
                >
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <h4 className="font-medium text-navy">{goal.title}</h4>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(goal.status)}`}>
                      {goal.status.replace('_', ' ')}
                    </span>
                  </div>
                  <ProgressBar percentage={goal.progressPercentage} />
                  <div className="flex items-center justify-between mt-2 text-sm text-warm-gray">
                    <span>{goal.progressPercentage}% complete</span>
                    {goal.targetDate && (
                      <span>Target: {new Date(goal.targetDate).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default IEPGoalsSummary;

